/* SubjectPicker — searchable dropdown of teacher's subjects with inline
 * "+ Add subject" (and "+ Add grade") flows.
 *
 * Backed by /api/subjects and /api/grades. Selection is a plain string id
 * lifted to the parent via onChange.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { BookOpen, ChevronDown, ChevronLeft, Search, Plus, X, Check, Loader2, GraduationCap } from "lucide-react";
import api from "@/lib/api";

export default function SubjectPicker({ value, onChange, buttonClassName = "", size = "sm", showLabel = true }) {
  const [open, setOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("list"); // list | add-subject | add-grade
  const [subjName, setSubjName] = useState("");
  const [subjGradeId, setSubjGradeId] = useState("");
  const [gradeName, setGradeName] = useState("");
  const [busy, setBusy] = useState(false);
  const [gradeMenuOpen, setGradeMenuOpen] = useState(false);
  const rootRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([
        api.get("/subjects"),
        api.get("/grades"),
      ]);
      setSubjects(s.data.subjects || []);
      setGrades(g.data.grades || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) refresh(); }, [open]);
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) { setOpen(false); setGradeMenuOpen(false); } };
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (gradeMenuOpen) return setGradeMenuOpen(false);
      if (mode !== "list") return setMode(mode === "add-grade" ? "add-subject" : "list");
      setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onEsc);
    }
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open, mode, gradeMenuOpen]);

  const filtered = useMemo(() => {
    const qs = q.trim().toLowerCase();
    if (!qs) return subjects;
    return subjects.filter((s) => (s.name || "").toLowerCase().includes(qs) || (s.grade_name || "").toLowerCase().includes(qs));
  }, [subjects, q]);

  const current = subjects.find((s) => s.id === value);

  const submitSubject = async () => {
    if (!subjName.trim()) return toast.error("Subject name required");
    setBusy(true);
    try {
      const { data } = await api.post("/subjects", { name: subjName.trim(), grade_id: subjGradeId });
      toast.success(`Added · ${data.subject.name}`);
      await refresh();
      setSubjName(""); setSubjGradeId(""); setMode("list");
      onChange?.(data.subject.id, data.subject);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add subject");
    } finally { setBusy(false); }
  };

  const submitGrade = async () => {
    if (!gradeName.trim()) return toast.error("Grade name required");
    setBusy(true);
    try {
      const { data } = await api.post("/grades", { name: gradeName.trim() });
      toast.success(`Added · ${data.grade.name}`);
      await refresh();
      setSubjGradeId(data.grade.id);
      setGradeName(""); setMode("add-subject");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add grade");
    } finally { setBusy(false); }
  };

  const label = current ? `${current.name}${current.grade_name ? ` · ${current.grade_name}` : ""}` : (showLabel ? "Pick subject" : "Subject");
  const sizeCls = size === "sm" ? "text-xs px-3 py-1.5" : "text-sm px-4 py-2";

  return (
    <div className="relative" ref={rootRef} data-testid="subject-picker">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="subject-picker-toggle"
        className={`${sizeCls} inline-flex items-center gap-1.5 border rounded-md text-neutral-800 ${buttonClassName || (current ? "border-neutral-300 hover:border-[#0A0C10] bg-white" : "border-neutral-300 hover:border-[#0A0C10] bg-white")}`}
      >
        <BookOpen size={12} className="opacity-60" />
        <span className={`${current ? "font-semibold" : "opacity-70"} truncate max-w-[220px]`}>{label}</span>
        <ChevronDown size={11} />
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.15 }}
          className="absolute left-0 right-auto sm:right-0 sm:left-auto mt-1.5 z-[80] w-[340px] max-w-[calc(100vw-2rem)] bg-[#0F1218] text-white border border-white/10 rounded-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)]"
          data-testid="subject-picker-panel"
        >
          {/* HEADER — mode-aware title + back / close */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10 bg-white/[.03]">
            {mode !== "list" && (
              <button onClick={() => setMode(mode === "add-grade" ? "add-subject" : "list")}
                data-testid="subject-picker-back"
                className="w-6 h-6 rounded flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10"
              ><ChevronLeft size={13} /></button>
            )}
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/60 flex-1">
              {mode === "list" ? "Choose subject" : mode === "add-subject" ? "New subject" : "New grade"}
            </div>
            <button onClick={() => { setOpen(false); setMode("list"); }}
              data-testid="subject-picker-close"
              className="w-6 h-6 rounded flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10"
            ><X size={13} /></button>
          </div>

          {/* ─── LIST MODE ─── */}
          {mode === "list" && (
            <>
              <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2">
                <Search size={13} className="text-white/40" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search your subjects…"
                  data-testid="subject-picker-search"
                  className="flex-1 text-sm bg-transparent outline-none text-white placeholder:text-white/30"
                />
              </div>
              <div className="max-h-[280px] overflow-y-auto p-1">
                {loading && <div className="p-3 text-xs text-white/50 inline-flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Loading…</div>}
                {!loading && filtered.length === 0 && (
                  <div className="p-4 text-xs text-white/50 text-center">
                    No subjects{q ? " match your search" : " yet"}.<br /><span className="text-white/40">Use the button below to add one.</span>
                  </div>
                )}
                {filtered.map((s) => (
                  <button key={s.id} onClick={() => { onChange?.(s.id, s); setOpen(false); }}
                    data-testid={`subject-option-${s.id}`}
                    className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between transition-colors ${value === s.id ? "bg-white/10" : "hover:bg-white/5"}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{s.name}</div>
                      {s.grade_name && (
                        <div className="text-[10px] font-mono uppercase tracking-wider text-white/50 inline-flex items-center gap-1 mt-0.5">
                          <GraduationCap size={10} />{s.grade_name}
                        </div>
                      )}
                    </div>
                    {value === s.id && <Check size={14} className="text-[#10B981] shrink-0 ml-2" />}
                  </button>
                ))}
              </div>
              <button onClick={() => setMode("add-subject")} data-testid="subject-picker-add"
                className="w-full text-left px-3 py-2.5 border-t border-white/10 text-sm font-semibold bg-[#6366F1]/10 text-[#A5B4FC] hover:bg-[#6366F1]/20 hover:text-white inline-flex items-center gap-1.5 transition-colors">
                <Plus size={13} /> Add a new subject
              </button>
            </>
          )}

          {/* ─── ADD SUBJECT MODE ─── */}
          {mode === "add-subject" && (
            <div className="p-3 space-y-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-1.5">Subject name</div>
                <input autoFocus value={subjName} onChange={(e) => setSubjName(e.target.value)}
                  placeholder="e.g. Biology, Additional Math…"
                  data-testid="subject-picker-name-input"
                  onKeyDown={(e) => { if (e.key === "Enter") submitSubject(); }}
                  className="w-full text-sm bg-white/[.06] border border-white/15 focus:border-[#6366F1] rounded-md px-3 py-2 outline-none text-white placeholder:text-white/30 transition-colors" />
              </div>

              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-1.5">Grade (optional)</div>
                <div className="flex items-stretch gap-2">
                  {/* Custom grade dropdown (native <select> can't be dark-themed reliably) */}
                  <div className="relative flex-1">
                    <button onClick={() => setGradeMenuOpen((v) => !v)} data-testid="subject-picker-grade-select"
                      className="w-full text-sm bg-white/[.06] border border-white/15 hover:border-[#6366F1] rounded-md px-3 py-2 outline-none text-white text-left inline-flex items-center justify-between transition-colors">
                      <span className={subjGradeId ? "" : "text-white/40"}>
                        {subjGradeId ? (grades.find((g) => g.id === subjGradeId)?.name || "Grade") : "— No grade —"}
                      </span>
                      <ChevronDown size={12} className="text-white/50" />
                    </button>
                    <AnimatePresence>
                      {gradeMenuOpen && (
                        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                          className="absolute left-0 right-0 top-full mt-1 z-10 bg-[#0F1218] border border-white/15 rounded-md shadow-lg max-h-[180px] overflow-y-auto"
                          data-testid="subject-picker-grade-menu"
                        >
                          <button onClick={() => { setSubjGradeId(""); setGradeMenuOpen(false); }}
                            data-testid="subject-picker-grade-none"
                            className={`w-full text-left px-3 py-2 text-sm text-white/70 hover:bg-white/10 ${!subjGradeId ? "bg-white/5" : ""}`}
                          >— No grade —</button>
                          {grades.map((g) => (
                            <button key={g.id} onClick={() => { setSubjGradeId(g.id); setGradeMenuOpen(false); }}
                              data-testid={`subject-picker-grade-opt-${g.id}`}
                              className={`w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 inline-flex items-center gap-2 ${subjGradeId === g.id ? "bg-white/10" : ""}`}
                            >
                              <GraduationCap size={11} className="text-white/50" /> {g.name}
                            </button>
                          ))}
                          {grades.length === 0 && (
                            <div className="px-3 py-2 text-xs text-white/40">No grades yet — add one →</div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <button onClick={() => setMode("add-grade")} title="Add new grade" data-testid="subject-picker-add-grade"
                    className="w-10 shrink-0 rounded-md border border-[#6366F1]/40 bg-[#6366F1]/15 hover:bg-[#6366F1] text-[#A5B4FC] hover:text-white flex items-center justify-center transition-colors">
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              <button onClick={submitSubject} disabled={busy || !subjName.trim()} data-testid="subject-picker-submit-subject"
                className="w-full bg-[#FFD600] text-[#0A0C10] font-bold py-2.5 text-sm rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2 hover:brightness-110">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Create subject
              </button>
              <div className="text-[10px] text-white/40 text-center">Press <kbd className="px-1 py-0.5 bg-white/10 rounded text-white/60">Esc</kbd> to go back</div>
            </div>
          )}

          {/* ─── ADD GRADE MODE ─── */}
          {mode === "add-grade" && (
            <div className="p-3 space-y-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-1.5">Grade name</div>
                <input autoFocus value={gradeName} onChange={(e) => setGradeName(e.target.value)}
                  placeholder="e.g. Grade 9, Year 10, IGCSE Yr 11…"
                  onKeyDown={(e) => { if (e.key === "Enter") submitGrade(); }}
                  data-testid="subject-picker-grade-input"
                  className="w-full text-sm bg-white/[.06] border border-white/15 focus:border-[#6366F1] rounded-md px-3 py-2 outline-none text-white placeholder:text-white/30 transition-colors" />
              </div>
              <button onClick={submitGrade} disabled={busy || !gradeName.trim()} data-testid="subject-picker-submit-grade"
                className="w-full bg-[#6366F1] text-white font-bold py-2.5 text-sm rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2 hover:brightness-110">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Create grade
              </button>
              <div className="text-[10px] text-white/40 text-center">
                <ChevronLeft size={9} className="inline -mt-0.5" /> Back to subject · <kbd className="px-1 py-0.5 bg-white/10 rounded text-white/60">Esc</kbd> to close
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
